import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Factory, Plus, Trash2, Pencil, CheckCircle2, XCircle, Eye, FlaskConical, LayoutDashboard, ClipboardList, TrendingUp, Boxes, Percent, AlertTriangle, Settings2, Wallet } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select } from "../lib/ui";
import { JournalPreview } from "../components/JournalPreview";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { ProductSelect } from "../components/ProductSelect";

const STATUS_TONE = { DRAFT: "mute", CONFIRMED: "success", CANCELLED: "danger" };
const STATUS_LABEL = { DRAFT: "Draft", CONFIRMED: "Confirmed", CANCELLED: "Cancelled" };

export default function Production({ embedded = false }) {
  const { t } = useT();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const urlTab = new URLSearchParams(location.search).get("tab");
  const [tab, setTab] = useState(urlTab || "batch");
  const [costSettings, setCostSettings] = useState(false);
  const canDash = hasPermission("view_production_dashboard");
  const canManage = hasPermission("manage_production_recipes");

  useEffect(() => { if (urlTab && urlTab !== tab) setTab(urlTab); }, [urlTab]); // eslint-disable-line
  // When embedded inside the Central Production workspace we keep tab state local
  // (the parent owns the URL via ?view=engine) so we never navigate away.
  const selectTab = (k) => { setTab(k); if (!embedded) navigate(`/production?tab=${k}`); };

  const TABS = [
    { key: "batch", label: "Production Batches", icon: Factory },
    { key: "history", label: "Production History", icon: ClipboardList },
    { key: "recipes", label: "Production Recipes", icon: FlaskConical },
    ...(canDash ? [{ key: "dashboard", label: "Production Dashboard", icon: LayoutDashboard }] : []),
    { key: "hpp", label: "Production HPP", icon: Wallet },
  ];

  return (
    <div className="animate-fade-up">
      {!embedded && (
        <PageHeader title={t("Central Production")} subtitle={t("Central Kitchen production & transformation")} icon={Factory}>
          {canManage && <Button variant="outline" onClick={() => setCostSettings(true)} data-testid="cost-settings-btn"><Settings2 size={16} /> {t("Cost Settings")}</Button>}
        </PageHeader>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => selectTab(tb.key)} data-testid={`prod-tab-${tb.key}`}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${tab === tb.key ? "bg-primary text-white" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
            <tb.icon size={16} /> {t(tb.label)}
          </button>
        ))}
        {embedded && canManage && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => setCostSettings(true)} data-testid="cost-settings-btn"><Settings2 size={16} /> {t("Cost Settings")}</Button>
        )}
      </div>
      {(tab === "batch" || tab === "history") && <HistoryTab mode={tab} />}
      {tab === "recipes" && <RecipesTab />}
      {tab === "dashboard" && canDash && <ProductionDashboardTab />}
      {tab === "hpp" && <HppTab canManage={canManage} onOpenSettings={() => setCostSettings(true)} />}
      {costSettings && <CostSettingsModal onClose={() => setCostSettings(false)} />}
    </div>
  );
}

function CostSettingsModal({ onClose }) {
  const { t } = useT();
  const toast = useToast();
  const [pct, setPct] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/production/cost-settings").then(({ data }) => setPct(String(data.overhead_pct))); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/production/cost-settings", { overhead_pct: Number(pct) }); toast.success(t("Cost settings updated")); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };
  return (
    <Modal open onClose={onClose} size="sm" title={t("Production Cost Settings")}
      footer={<><Button variant="outline" onClick={onClose}>{t("Cancel")}</Button><Button onClick={save} loading={saving} data-testid="save-cost-settings">{t("Save")}</Button></>}>
      <div>
        <label className="cf-label">{t("Manufacturing Overhead (% of material cost)")}</label>
        <div className="relative">
          <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="cf-input pr-8" data-testid="cs-overhead-pct" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-mute">%</span>
        </div>
        <p className="mt-2 text-xs text-ink-mute">{t("Overhead is added to HPP only and does not affect inventory valuation.")}</p>
      </div>
    </Modal>
  );
}

/* ============================ Recipes ============================ */
function RecipesTab() {
  const { t } = useT();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_production_recipes");
  const [recipes, setRecipes] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => api.get("/production/recipes").then(({ data }) => setRecipes(data)), []);
  useEffect(() => {
    load();
    api.get("/products?active_only=true&visible_in=recipe_bom").then(({ data }) => setProducts(data));
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, [load]);

  if (!recipes) return <PageLoader />;
  // Master Data SSOT: Output must be Central Production Product; Inputs must be
  // Central Raw Material or Ready-to-Cook Ingredient.
  const outputProducts = products.filter((p) => p.product_type === "INTERNAL_FINISHED_GOOD");
  const inputProducts = products.filter((p) => p.track_inventory !== false
    && (p.product_type === "RAW_MATERIAL" || p.product_type === "PREPARED"));

  const remove = async (r) => {
    if (!window.confirm(t("Delete this recipe?"))) return;
    await api.delete(`/production/recipes/${r.id}`); toast.success(t("Recipe deleted")); load();
  };

  return (
    <div>
      {canManage && <div className="mb-3 flex justify-end"><Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-recipe"><Plus size={18} /> {t("New Recipe")}</Button></div>}
      {recipes.filter((r) => r.active !== false).length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="recipes-empty">{t("No production recipes yet")}</Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {recipes.filter((r) => r.active !== false).map((r) => (
            <Card key={r.id} className="p-4" data-testid={`recipe-${r.name}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-base font-bold text-ink">{r.name}</h3>
                  <p className="text-xs text-ink-mute">{t("Output")}: <span className="font-semibold text-primary">{r.output_product_name} · {fmtNum(r.output_quantity)} {fmtUnit(r.output_unit)}</span></p>
                </div>
                {canManage && (
                  <div className="flex gap-1">
                    <button onClick={() => { setEdit(r); setOpen(true); }} className="rounded-lg p-1.5 text-primary hover:bg-primary-light" data-testid={`edit-recipe-${r.name}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(r)} className="rounded-lg p-1.5 text-danger-text hover:bg-danger-light"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.inputs.map((i, idx) => (
                  <span key={idx} className="rounded-lg bg-stone-100 px-2 py-0.5 text-xs text-ink-soft">{i.product_name} · {fmtNum(i.quantity)} {fmtUnit(i.unit)}</span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
      {open && <RecipeModal recipe={edit} products={products} categories={categories} outputProducts={outputProducts} inputProducts={inputProducts} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function RecipeModal({ recipe, products, categories = [], outputProducts, inputProducts, onClose, onSaved }) {
  const { t } = useT();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => recipe ? {
    name: recipe.name, output_product_id: recipe.output_product_id, output_quantity: recipe.output_quantity,
    output_unit: recipe.output_unit || "", notes: recipe.notes || "", standard_labor_cost: recipe.standard_labor_cost ?? 0,
    inputs: recipe.inputs.map((i) => ({ product_id: i.product_id, quantity: i.quantity, unit: i.unit || "" })),
  } : { name: "", output_product_id: "", output_quantity: "", output_unit: "", notes: "", standard_labor_cost: "", inputs: [{ product_id: "", quantity: "", unit: "" }] });

  const setIn = (i, patch) => setForm((f) => ({ ...f, inputs: f.inputs.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const onPickInput = (i, pid) => { const p = products.find((x) => x.id === pid); setIn(i, { product_id: pid, unit: p?.unit || "" }); };
  const onPickOutput = (pid) => { const p = products.find((x) => x.id === pid); setForm((f) => ({ ...f, output_product_id: pid, output_unit: f.output_unit || p?.unit || "" })); };

  const save = async () => {
    const inputs = form.inputs.filter((i) => i.product_id && Number(i.quantity) > 0).map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity), unit: i.unit || null }));
    if (!form.name || !form.output_product_id || !Number(form.output_quantity) || inputs.length === 0) return toast.error(t("Fill name, output and at least one input"));
    setSaving(true);
    try {
      const payload = { name: form.name, output_product_id: form.output_product_id, output_quantity: Number(form.output_quantity), output_unit: form.output_unit || null, notes: form.notes || null, standard_labor_cost: Number(form.standard_labor_cost) || 0, inputs };
      if (recipe) await api.put(`/production/recipes/${recipe.id}`, payload);
      else await api.post("/production/recipes", payload);
      toast.success(recipe ? t("Recipe updated") : t("Recipe created")); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };

  return (
    <Modal open onClose={onClose} size="lg" title={recipe ? t("Edit Recipe") : t("New Recipe")}
      footer={<><Button variant="outline" onClick={onClose}>{t("Cancel")}</Button><Button onClick={save} loading={saving} data-testid="save-recipe">{t("Save")}</Button></>}>
      <div className="space-y-3">
        <div><label className="cf-label">{t("Recipe Name")}</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" data-testid="rf-name" /></div>
        <div className="rounded-xl border border-primary/20 bg-primary-light/30 p-3">
          <label className="cf-label">{t("Output Product")}</label>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-6"><ProductSelect products={outputProducts} categories={categories} value={form.output_product_id} onChange={onPickOutput} testid="rf-output-product" placeholder={t("Select Central Production Product...")} /></div>
            <div className="col-span-3"><input type="number" placeholder={t("Qty")} value={form.output_quantity} onChange={(e) => setForm({ ...form, output_quantity: e.target.value })} className="cf-input" data-testid="rf-output-qty" /></div>
            <div className="col-span-3"><input placeholder={t("Unit")} value={form.output_unit} onChange={(e) => setForm({ ...form, output_unit: e.target.value })} className="cf-input" /></div>
          </div>
          {outputProducts.length === 0 && (
            <p className="mt-2 text-xs text-danger-text" data-testid="rf-no-output-products">
              {t("No Central Production Product exists yet. Create one in Master Data first (classification = Central Production Product).")}
            </p>
          )}
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between"><label className="cf-label mb-0">{t("Input Materials")}</label>
            <button type="button" onClick={() => setForm((f) => ({ ...f, inputs: [...f.inputs, { product_id: "", quantity: "", unit: "" }] }))} className="rounded-lg border border-line px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-stone-50" data-testid="rf-add-input"><Plus size={13} className="inline" /> {t("Add Input")}</button>
          </div>
          <div className="space-y-2">
            {form.inputs.map((it, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`rf-input-${i}`}>
                <div className="col-span-6"><ProductSelect products={inputProducts} categories={categories} value={it.product_id} onChange={(pid) => onPickInput(i, pid)} testid={`rf-input-product-${i}`} placeholder={t("Pilih bahan (Bahan Baku / Bahan Siap Masak)...")} /></div>
                <div className="col-span-3"><input type="number" placeholder={t("Qty")} value={it.quantity} onChange={(e) => setIn(i, { quantity: e.target.value })} className="cf-input" data-testid={`rf-input-qty-${i}`} /></div>
                <div className="col-span-2 text-xs text-ink-mute">{fmtUnit(it.unit)}</div>
                <div className="col-span-1"><button type="button" onClick={() => setForm((f) => ({ ...f, inputs: f.inputs.filter((_, idx) => idx !== i) }))} className="rounded-lg p-1.5 text-danger-text hover:bg-danger-light"><Trash2 size={14} /></button></div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="cf-label">{t("Standard Labor Cost")} (Rp)</label><input type="number" value={form.standard_labor_cost} onChange={(e) => setForm({ ...form, standard_labor_cost: e.target.value })} className="cf-input" data-testid="rf-labor" /></div>
          <div><label className="cf-label">{t("Notes")}</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="cf-input" /></div>
        </div>
      </div>
    </Modal>
  );
}

/* ============================ History / Batches ============================ */
function HistoryTab({ mode = "batch" }) {
  const { t } = useT();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("create_production") && mode === "batch";
  const [batches, setBatches] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [create, setCreate] = useState(false);
  const [confirmB, setConfirmB] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/production/batches").then(({ data }) => setBatches(data)), []);
  useEffect(() => {
    load();
    api.get("/production/recipes").then(({ data }) => setRecipes(data.filter((r) => r.active !== false)));
  }, [load]);

  if (!batches) return <PageLoader />;
  // Batch tab focuses on active work (drafts first); History shows the full archive.
  const list = mode === "history" ? batches : [...batches].sort((a, b) => (a.status === "DRAFT" ? -1 : 1) - (b.status === "DRAFT" ? -1 : 1));

  return (
    <div>
      {canCreate && <div className="mb-3 flex justify-end"><Button onClick={() => setCreate(true)} data-testid="new-production"><Plus size={18} /> {t("New Production")}</Button></div>}
      {batches.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="batches-empty">{t("No production batches yet")}</Card>
      ) : (
        <Card className="overflow-hidden p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <th className="px-4 py-3">{t("Production No")}</th><th className="hidden px-4 py-3 sm:table-cell">{t("Date")}</th>
            <th className="px-4 py-3">{t("Recipe")}</th><th className="px-4 py-3 text-right">{t("Planned")}</th>
            <th className="px-4 py-3 text-right">{t("Actual")}</th><th className="px-4 py-3 text-right">{t("Yield")}</th>
            <th className="hidden px-4 py-3 md:table-cell">{t("Created By")}</th><th className="px-4 py-3 text-center">{t("Status")}</th>
            <th className="px-4 py-3 text-right">{t("Actions")}</th></tr></thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`batch-${b.production_no}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold">{b.production_no}</td>
                <td className="hidden px-4 py-3 text-xs text-ink-mute sm:table-cell">{(b.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 font-semibold">{b.recipe_name}<span className="block text-xs font-normal text-ink-mute">{b.output_product_name}</span></td>
                <td className="px-4 py-3 text-right">{fmtNum(b.planned_output_qty)} {fmtUnit(b.output_unit)}</td>
                <td className="px-4 py-3 text-right">{b.actual_output_qty != null ? `${fmtNum(b.actual_output_qty)} ${fmtUnit(b.output_unit)}` : "—"}</td>
                <td className="px-4 py-3 text-right font-bold">{b.yield_pct != null ? `${b.yield_pct}%` : "—"}</td>
                <td className="hidden px-4 py-3 text-xs text-ink-soft md:table-cell">{b.created_by_name}</td>
                <td className="px-4 py-3 text-center"><Badge tone={STATUS_TONE[b.status]}>{t(STATUS_LABEL[b.status])}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => setDetail(b)} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" title={t("Detail")} data-testid={`detail-${b.production_no}`}><Eye size={15} /></button>
                    {canCreate && b.status === "DRAFT" && (
                      <button onClick={() => setConfirmB(b)} className="rounded-lg bg-success/10 px-2 py-1 text-xs font-semibold text-success-text hover:bg-success/20" data-testid={`confirm-${b.production_no}`}>{t("Confirm")}</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div></Card>
      )}
      {create && <BatchModal recipes={recipes} onClose={() => setCreate(false)} onCreated={() => { setCreate(false); load(); }} />}
      {confirmB && <ConfirmModal batch={confirmB} onClose={() => setConfirmB(null)} onConfirmed={() => { setConfirmB(null); load(); }} onCancelled={() => { setConfirmB(null); load(); }} />}
      {detail && <BatchDetail batch={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function BatchModal({ recipes, onClose, onCreated }) {
  const { t } = useT();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [recipeId, setRecipeId] = useState(recipes[0]?.id || "");
  const [mult, setMult] = useState("1");
  const recipe = recipes.find((r) => r.id === recipeId);
  const m = Number(mult) || 0;

  const create = async () => {
    if (!recipeId || m <= 0) return toast.error(t("Select a recipe and valid multiplier"));
    setSaving(true);
    try {
      await api.post("/production/batches", { recipe_id: recipeId, multiplier: m });
      toast.success(t("Production draft created")); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };

  return (
    <Modal open onClose={onClose} title={t("New Production")} size="md"
      footer={<><Button variant="outline" onClick={onClose}>{t("Cancel")}</Button><Button onClick={create} loading={saving} data-testid="create-batch">{t("Create Draft")}</Button></>}>
      {recipes.length === 0 ? <p className="py-6 text-center text-ink-mute">{t("Create a production recipe first")}</p> : (
        <div className="space-y-3">
          <Select label={t("Recipe")} value={recipeId} onChange={(e) => setRecipeId(e.target.value)} data-testid="bf-recipe">
            {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
          <div><label className="cf-label">{t("Batch Multiplier")}</label><input type="number" value={mult} onChange={(e) => setMult(e.target.value)} className="cf-input" data-testid="bf-multiplier" /></div>
          {recipe && (
            <div className="rounded-xl border border-line p-3 text-sm">
              <p className="mb-2 font-semibold text-primary">{t("Planned Output")}: {fmtNum((recipe.output_quantity || 0) * m)} {fmtUnit(recipe.output_unit)} {recipe.output_product_name}</p>
              <p className="cf-label">{t("Will consume")}:</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {recipe.inputs.map((i, idx) => <span key={idx} className="rounded-lg bg-stone-100 px-2 py-0.5 text-xs">{i.product_name} · {fmtNum((i.quantity || 0) * m)} {fmtUnit(i.unit)}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function ConfirmModal({ batch, onClose, onConfirmed, onCancelled }) {
  const { t } = useT();
  const toast = useToast();
  const [actual, setActual] = useState(String(batch.planned_output_qty ?? ""));
  const [labor, setLabor] = useState(String(batch.default_labor_cost ?? 0));
  const [overheadPct, setOverheadPct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [shortages, setShortages] = useState(null);
  const planned = Number(batch.planned_output_qty) || 0;
  const yieldPreview = planned > 0 && Number(actual) > 0 ? Math.round((Number(actual) / planned) * 1000) / 10 : null;

  useEffect(() => { api.get("/production/cost-settings").then(({ data }) => setOverheadPct(data.overhead_pct)).catch(() => {}); }, []);

  const confirm = async () => {
    if (!(Number(actual) > 0)) return toast.error(t("Enter a valid actual output"));
    setSaving(true); setShortages(null);
    try {
      const { data } = await api.post(`/production/batches/${batch.id}/confirm`, { actual_output_qty: Number(actual), labor_cost: Number(labor) || 0 });
      toast.success(`${t("Production confirmed")} · ${t("Yield")} ${data.yield_pct}% · HPP/${t("unit")} ${fmtRp(data.hpp_per_unit)}`); onConfirmed();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.shortages) { setShortages(d.shortages); toast.error(d.message); }
      else toast.error(d || t("Failed"));
    }
    setSaving(false);
  };

  const cancelBatch = async () => {
    if (!window.confirm(t("Cancel this draft batch?"))) return;
    await api.post(`/production/batches/${batch.id}/cancel`); toast.success(t("Batch cancelled")); onCancelled();
  };

  return (
    <Modal open onClose={onClose} title={`${t("Confirm Production")} · ${batch.production_no}`} size="md"
      footer={<>
        <Button variant="outline" onClick={cancelBatch} data-testid="cancel-batch"><XCircle size={15} /> {t("Cancel Batch")}</Button>
        <Button onClick={confirm} loading={saving} data-testid="confirm-batch"><CheckCircle2 size={15} /> {t("Confirm")}</Button>
      </>}>
      <div className="space-y-3">
        <div className="rounded-xl border border-line p-3 text-sm">
          <p>{t("Recipe")}: <b>{batch.recipe_name}</b></p>
          <p>{t("Planned Output")}: <b>{fmtNum(planned)} {fmtUnit(batch.output_unit)}</b> {batch.output_product_name}</p>
        </div>
        <div><label className="cf-label">{t("Actual Output Quantity")}</label>
          <input type="number" value={actual} onChange={(e) => setActual(e.target.value)} className="cf-input" data-testid="cf-actual" autoFocus /></div>
        {yieldPreview != null && <p className="text-sm font-semibold text-primary">{t("Yield")}: {yieldPreview}%</p>}
        <div><label className="cf-label">{t("Labor Cost")} (Rp)</label>
          <input type="number" value={labor} onChange={(e) => setLabor(e.target.value)} className="cf-input" data-testid="cf-labor" />
          <p className="mt-1 text-[11px] text-ink-mute">{t("Default from recipe · override here if needed")}</p></div>
        <p className="text-xs text-ink-mute">{t("Overhead")}: {overheadPct != null ? `${overheadPct}% ${t("of material cost")}` : "—"} · {t("HPP = Material + Labor + Overhead (reporting only — does not change inventory value)")}</p>
        {shortages && (
          <div className="rounded-xl border border-danger/30 bg-danger-light/40 p-3" data-testid="shortage-error">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-danger-text"><AlertTriangle size={15} /> {t("Stok tidak mencukupi untuk produksi")}</p>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-ink-mute"><th className="py-1">{t("Material")}</th><th className="py-1 text-right">{t("Current Stock")}</th><th className="py-1 text-right">{t("Required")}</th><th className="py-1 text-right">{t("Shortage")}</th></tr></thead>
              <tbody>{shortages.map((s, i) => (
                <tr key={i} className="border-t border-danger/10"><td className="py-1 font-semibold">{s.product_name}</td><td className="py-1 text-right">{fmtNum(s.current_stock)} {fmtUnit(s.unit)}</td><td className="py-1 text-right">{fmtNum(s.required)} {fmtUnit(s.unit)}</td><td className="py-1 text-right font-bold text-danger-text">{fmtNum(s.shortage)} {fmtUnit(s.unit)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function BatchDetail({ batch, onClose }) {
  const { t } = useT();
  const [moves, setMoves] = useState(null);
  useEffect(() => {
    if (batch.status === "CONFIRMED") api.get(`/stock-movements?ref_id=${batch.id}`).then(({ data }) => setMoves(data)).catch(() => setMoves([]));
    else setMoves([]);
  }, [batch]);

  return (
    <Modal open onClose={onClose} size="lg" title={`${t("Production Detail")} · ${batch.production_no}`}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t("Status")} value={t(STATUS_LABEL[batch.status])} />
          <Stat label={t("Planned")} value={`${fmtNum(batch.planned_output_qty)} ${fmtUnit(batch.output_unit)}`} />
          <Stat label={t("Actual")} value={batch.actual_output_qty != null ? `${fmtNum(batch.actual_output_qty)} ${fmtUnit(batch.output_unit)}` : "—"} />
          <Stat label={t("Yield")} value={batch.yield_pct != null ? `${batch.yield_pct}%` : "—"} tone="primary" />
        </div>

        <div>
          <p className="cf-label mb-1.5">{t("Consumed Materials")}</p>
          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line bg-stone-50 text-left text-xs text-ink-mute"><th className="px-3 py-2">{t("Material")}</th><th className="px-3 py-2 text-right">{t("Qty")}</th><th className="px-3 py-2 text-right">{t("Unit Cost")}</th><th className="px-3 py-2 text-right">{t("Total Cost")}</th></tr></thead>
              <tbody>{batch.inputs.map((c, i) => (
                <tr key={i} className="border-b border-line/50"><td className="px-3 py-2 font-medium">{c.product_name}</td><td className="px-3 py-2 text-right">{fmtNum(c.quantity)} {fmtUnit(c.unit)}</td><td className="px-3 py-2 text-right text-ink-soft">{c.unit_cost != null ? fmtRp(c.unit_cost) : "—"}</td><td className="px-3 py-2 text-right font-semibold">{c.total_cost != null ? fmtRp(c.total_cost) : "—"}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        {batch.status === "CONFIRMED" && (
          <div className="rounded-xl border border-primary/20 bg-primary-light/30 p-3">
            <p className="cf-label mb-1.5">{t("Cost Rollup Summary")}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
              <span>{t("Total Input Cost")}: <b>{fmtRp(batch.input_cost_total)}</b></span>
              <span>{t("Output")}: <b>{fmtNum(batch.actual_output_qty)} {fmtUnit(batch.output_unit)} {batch.output_product_name}</b></span>
              <span>{t("Output Unit Cost")}: <b className="text-primary">{fmtRp(batch.output_unit_cost)}</b></span>
            </div>
          </div>
        )}

        {batch.status === "CONFIRMED" && <JournalPreview module="production" docId={batch.id} />}

        {batch.status === "CONFIRMED" && batch.hpp_total != null && (
          <div className="rounded-xl border border-line p-3" data-testid="hpp-breakdown">
            <p className="cf-label mb-2 flex items-center gap-1.5"><Wallet size={14} className="text-primary" /> {t("HPP Breakdown")}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <HppRow label={t("Material Cost")} value={fmtRp(batch.direct_material_cost)} />
              <HppRow label={t("Labor Cost")} value={fmtRp(batch.direct_labor_cost)} />
              <HppRow label={`${t("Overhead Cost")}${batch.overhead_pct != null ? ` (${batch.overhead_pct}%)` : ""}`} value={fmtRp(batch.manufacturing_overhead)} />
              <HppRow label={t("Total HPP")} value={fmtRp(batch.hpp_total)} strong />
              <HppRow label={t("HPP per Unit")} value={fmtRp(batch.hpp_per_unit)} strong tone="primary" />
              <HppRow label={t("Transfer Price")} value={batch.transfer_price > 0 ? fmtRp(batch.transfer_price) : "—"} />
            </div>
            {batch.transfer_price > 0 && batch.gross_margin != null && (
              <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg bg-stone-50 px-3 py-2 text-sm" data-testid="gross-margin">
                <span>{t("Gross Margin")}/{t("unit")}: <b className={batch.gross_margin >= 0 ? "text-success-text" : "text-danger-text"}>{fmtRp(batch.gross_margin)}</b></span>
                <span>{t("Margin")}: <b className={batch.gross_margin_pct >= 0 ? "text-success-text" : "text-danger-text"}>{batch.gross_margin_pct}%</b></span>
              </div>
            )}
            <p className="mt-2 text-[11px] text-ink-mute">{t("HPP is a reporting figure. Inventory is valued at material cost only.")}</p>
          </div>
        )}

        {moves && moves.length > 0 && (
          <div>
            <p className="cf-label mb-1.5">{t("Stock Ledger References")}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="ledger-refs">
              {moves.map((m) => (
                <span key={m.id} className={`rounded-lg px-2 py-1 text-xs ${m.direction === "in" ? "bg-success/10 text-success-text" : "bg-warn/10 text-warn-text"}`}>
                  {m.reference_no} · {m.product_name} {m.direction === "in" ? "+" : "−"}{fmtNum(m.quantity)}
                </span>
              ))}
            </div>
          </div>
        )}
        {batch.notes && <p className="text-sm text-ink-soft">{t("Notes")}: {batch.notes}</p>}
      </div>
    </Modal>
  );
}

/* ============================ Dashboard ============================ */
function ProductionDashboardTab() {
  const { t } = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/production/dashboard").then(({ data }) => setD(data)).catch(() => setD({})); }, []);
  if (!d) return <PageLoader />;

  return (
    <div className="space-y-4" data-testid="production-dashboard">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KpiCard icon={Factory} label={t("Production Today")} value={d.production_today ?? 0} tone="primary" testid="pdash-today" />
        <KpiCard icon={ClipboardList} label={t("Production This Month")} value={d.production_month ?? 0} tone="info" testid="pdash-month" />
        <KpiCard icon={Percent} label={t("Average Yield")} value={d.average_yield_pct != null ? `${d.average_yield_pct}%` : "—"} tone="success" testid="pdash-yield" />
        <KpiCard icon={TrendingUp} label={t("Production Variance")} value={fmtNum(d.production_variance ?? 0)} tone="warn" testid="pdash-variance" />
      </div>
      <Card className="p-4 sm:p-5">
        <div className="mb-3 flex items-center gap-2"><Boxes size={16} className="text-primary" /><h3 className="font-heading text-sm font-extrabold text-ink">{t("Top Produced Items")}</h3></div>
        {(d.top_produced || []).length === 0 ? <p className="py-6 text-center text-sm text-ink-mute">{t("No confirmed production yet")}</p> : (
          <div className="space-y-2.5">
            {d.top_produced.map((p, i) => {
              const max = d.top_produced[0].qty || 1;
              return (
                <div key={i} data-testid={`pdash-top-${i}`}>
                  <div className="mb-1 flex justify-between text-xs"><span className="font-semibold text-ink">{p.product_name}</span><span className="font-bold">{fmtNum(p.qty)} · {p.batches}x</span></div>
                  <div className="h-2 overflow-hidden rounded-full bg-stone-100"><div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(6, (p.qty / max) * 100)}%` }} /></div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ============================ HPP (per confirmed batch) ============================ */
function HppTab({ canManage, onOpenSettings }) {
  const { t } = useT();
  const [batches, setBatches] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/production/batches?status=CONFIRMED").then(({ data }) => setBatches(data)); }, []);
  if (!batches) return <PageLoader />;

  return (
    <div>
      {canManage && <div className="mb-3 flex justify-end"><Button variant="outline" onClick={onOpenSettings} data-testid="hpp-settings"><Settings2 size={16} /> {t("Cost Settings")}</Button></div>}
      {batches.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="hpp-empty">{t("No confirmed production yet")}</Card>
      ) : (
        <Card className="overflow-hidden p-0"><div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <th className="px-4 py-3">{t("Production No")}</th>
            <th className="px-4 py-3">{t("Output")}</th>
            <th className="hidden px-4 py-3 text-right sm:table-cell">{t("Material Cost")}</th>
            <th className="hidden px-4 py-3 text-right md:table-cell">{t("Labor Cost")}</th>
            <th className="hidden px-4 py-3 text-right md:table-cell">{t("Overhead Cost")}</th>
            <th className="px-4 py-3 text-right">{t("Total HPP")}</th>
            <th className="px-4 py-3 text-right">{t("HPP per Unit")}</th>
            <th className="hidden px-4 py-3 text-right lg:table-cell">{t("Margin")}</th>
            <th className="px-4 py-3 text-right"></th>
          </tr></thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`hpp-row-${b.production_no}`}>
                <td className="px-4 py-3 font-mono text-xs font-bold">{b.production_no}<span className="block font-sans text-[11px] font-normal text-ink-mute">{(b.confirmed_at || b.created_at || "").slice(0, 10)}</span></td>
                <td className="px-4 py-3 font-semibold">{b.output_product_name}<span className="block text-[11px] font-normal text-ink-mute">{fmtNum(b.actual_output_qty)} {fmtUnit(b.output_unit)}</span></td>
                <td className="hidden px-4 py-3 text-right text-ink-soft sm:table-cell">{fmtRp(b.direct_material_cost ?? b.input_cost_total)}</td>
                <td className="hidden px-4 py-3 text-right text-ink-soft md:table-cell">{fmtRp(b.direct_labor_cost)}</td>
                <td className="hidden px-4 py-3 text-right text-ink-soft md:table-cell">{fmtRp(b.manufacturing_overhead)}</td>
                <td className="px-4 py-3 text-right font-semibold">{fmtRp(b.hpp_total)}</td>
                <td className="px-4 py-3 text-right font-bold text-primary">{fmtRp(b.hpp_per_unit)}</td>
                <td className="hidden px-4 py-3 text-right lg:table-cell">{b.gross_margin_pct != null ? <span className={b.gross_margin_pct >= 0 ? "font-semibold text-success-text" : "font-semibold text-danger-text"}>{b.gross_margin_pct}%</span> : "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setDetail(b)} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hpp-detail-${b.production_no}`}><Eye size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table></div></Card>
      )}
      {detail && <BatchDetail batch={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}


function HppRow({ label, value, strong, tone }) {
  return (
    <div className={`rounded-lg border border-line px-2.5 py-1.5 ${strong ? "bg-white" : ""}`}>
      <p className="text-[11px] text-ink-mute">{label}</p>
      <p className={`text-sm ${strong ? "font-bold" : "font-semibold"} ${tone === "primary" ? "text-primary" : "text-ink"}`}>{value}</p>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-line bg-white p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-mute">{label}</p>
      <p className={`mt-0.5 text-base font-bold ${tone === "primary" ? "text-primary" : "text-ink"}`}>{value}</p>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = { primary: "bg-primary-light text-primary", info: "bg-blue-50 text-blue-600", success: "bg-success-light text-success-text", warn: "bg-amber-50 text-amber-600" };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
        <div><p className="text-xs font-semibold uppercase text-ink-mute">{label}</p><p className="text-xl font-bold text-ink">{value}</p></div>
      </div>
    </Card>
  );
}
